import { LitElement, html } from 'lit';

export default class BevrTemplate extends LitElement {
  static properties = {
    attr: { type: Object },
  };

  attributeChangedCallback(name, oldValue, newValue) {
    // Update attribute object
    this.attr[name] = newValue;
    this.requestUpdate(name, oldValue)
  }

  /**
   * Creates an instance of BevrTemplate.
   * @param {Object.<string, {template: string, registerAttributes?: function(): void}>} templates - The templates object.
   * @throws {Error} If no templates are provided.
   */
  constructor(templates) {
    if (!templates) {
      throw new Error('No templates provided');
    }

    // ensure all templates are valid
    for (const type in templates) {
      if (!templates[type].template) {
        throw new Error(`Invalid template for type: ${type}`);
      }
    }

    super();
    this.attachShadow({ mode: 'open' });
    this.loadStyles();
    this.templates = templates;
    this.attr = {};
  }

  connectedCallback() {
    super.connectedCallback();
    for (const attr of this.getAttributeNames()) {
      this.attr[attr] = this.getAttribute(attr);
    }
  }

  get type() {
    return this.getAttribute('type');
  }

  // Loads all stylesheets into shadowRoot
  loadStyles() {
    const styles = Array.from(document.styleSheets).map(styleSheet => {
      try {
        return Array.from(styleSheet.cssRules).map(rule => rule.cssText).join('\n');
      } catch (e) {
        console.warn('Could not access stylesheet:', styleSheet.href);
        return '';
      }
    }).join('\n');

    const styleElement = document.createElement('style');
    styleElement.textContent = styles;
    this.shadowRoot.appendChild(styleElement); // Append styles to shadowRoot
  }

  render() {
    function renderTemplate(template, ctx) {
      // Dynamically create a function that can render the template with the given context
      return new Function('html', 'ctx', template)(html, ctx);
    }

    if (!this.templates || !this.type) {
      console.error('No templates or type found');
      return html``;
    }

    const template = this.templates[this.type];
    const selectedTemplate = template?.template;
    if (!template || !selectedTemplate) {
      console.error('No template found for type:', this.type);
      return html``;
    }

    template.registerAttributes?.bind(this)(); // Bind custom attributes

    return renderTemplate(selectedTemplate, this);
  }
}